import { filter } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import { LocaleService } from '@sm360/widget-commons-core-services';
import { PurchaseMethods, Quote, ModelYear } from '@sm360/widget-commons-vehicle';
import {
    AnalyticsService,
    TRACKING_TAGS as TAGS,
    ADOBE_TAGS,
    AdobeService,
    ADOBE_VARIANT_CONSTANTS,
    ShiftDigitalService,
} from '@sm360/widget-commons-general';

import OptionAccessory from 'build-and-quote/components/OptionsAccessories/OptionAccessory/OptionAccessory.jsx';
import OPTION_CATEGORY from 'build-and-quote/constants/optionCategory.js';

import 'build-and-quote/components/OptionsAccessories/OptionsAccessories.scss';

const OptionCategory = ({
    isLease,
    isReadOnly,
    selectedFinanceTerm,
    paymentFrequencyLabel,
    taxesFactor,
    taxCodes,
    optionCategory,
    selectOption,
    modelYear,
    hasAdobeTagging,
}) => {
    useEffect(() => {
        if (hasAdobeTagging) {
            AdobeService.eventTrigger(
                ADOBE_TAGS.BUILDER_STEPS('options and accessories'),
                ADOBE_VARIANT_CONSTANTS.GLOBAL_OVERLAY
            );
        }
    }, []);
    const visibleOptions = filter(optionCategory.itemsPackages, (option) => !option.isConfigurationOption);
    const optionCategoryLength = visibleOptions.length;
    if (!optionCategoryLength) {
        return null;
    }

    const optionType =
        optionCategory.id === OPTION_CATEGORY.ACCESSORIES
            ? {
                  singular: LocaleService.t('optionsPackageSection.accessory'),
                  plural: LocaleService.t('optionsPackageSection.accessories'),
              }
            : {
                  singular: LocaleService.t('optionsPackageSection.option'),
                  plural: LocaleService.t('optionsPackageSection.options'),
              };

    const optionCategorySubtitle = LocaleService.t('optionsPackageSection.subtitle', {
        optionlength: `${optionCategoryLength}`,
        optionType: optionCategoryLength > 1 ? optionType.plural : optionType.singular,
    });

    const optionCategoryTitle =
        optionCategory.id === 1
            ? LocaleService.t('optionsPackageSection.title.OPTIONS')
            : LocaleService.t('optionsPackageSection.title.ACCESSORIES');

    const getSelectedPurchaseMethod = () => {
        let dealType = 'cash';

        if (isLease) {
            dealType = 'lease';
        } else if (selectedFinanceTerm) {
            dealType = 'finance';
        }

        return dealType;
    };

    const pullSelectedOption = (value, description) => {
        ShiftDigitalService.updateDrAddToCart(
            null,
            null,
            getSelectedPurchaseMethod(),
            description,
            'Accessories',
            modelYear
        );
        if (optionCategory.id === 1) {
            AnalyticsService.ga4Trigger(TAGS.OPTION(value, modelYear.model, modelYear.year));
        } else {
            AnalyticsService.ga4Trigger(TAGS.ACCESSORY(value, modelYear.model, modelYear.year));
        }
    };

    const protectionPackages = visibleOptions.map((option) => {
        return (
            <OptionAccessory
                key={option.selectionCode}
                isLease={isLease}
                isReadOnly={isReadOnly}
                selectedFinanceTerm={selectedFinanceTerm}
                paymentFrequencyLabel={paymentFrequencyLabel}
                taxesFactor={taxesFactor}
                taxCodes={taxCodes}
                optionCategory={optionCategory}
                option={option}
                optionItems={optionCategory.getItemsPackageContent(option.selectionCode)}
                selectOption={selectOption}
                modelYear={modelYear}
                setAnalyticsValue={pullSelectedOption}
            />
        );
    });

    return (
        <div>
            <h4 className="sr-subtitle-1 is-centered">
                {optionCategoryTitle} {optionCategorySubtitle}
            </h4>
            <div className="accessory_wrapper--listing"> {protectionPackages} </div>
        </div>
    );
};

OptionCategory.propTypes = {
    isLease: PropTypes.bool.isRequired,
    isReadOnly: PropTypes.bool.isRequired,
    optionCategory: PropTypes.instanceOf(Quote.QuoteItemCategoryModel).isRequired,
    paymentFrequencyLabel: PropTypes.string.isRequired,
    selectedFinanceTerm: PropTypes.instanceOf(PurchaseMethods.TermModel).isRequired,
    selectOption: PropTypes.func.isRequired,
    taxCodes: PropTypes.string.isRequired,
    taxesFactor: PropTypes.number.isRequired,
    modelYear: PropTypes.instanceOf(ModelYear.ModelYearModel).isRequired,
    hasAdobeTagging: PropTypes.bool.isRequired,
};

OptionCategory.displayName = 'OptionCategory';

export default OptionCategory;
